<template>
    <div class="c-google-map__container">
        <div class="c-google-map__actions c-location-controls" v-show="!isLoading">
            <div class="c-location-controls__response" v-if="searchResponse">
                <h4>{{ searchResponse }}</h4>
            </div>

            <div class="c-location-controls__wrapper">
                <button class="c-location-controls__locator c-btn c-btn--ghost" @click="locateUser" title="Use current location">
                    <i class="far fa-map-marker-alt"></i>
                </button>
                <label class="u-hidden-visually" for="map-search-input">Enter a location or postcode</label>
                <input id="map-search-input" class="c-location-controls__location-input" type="text" ref="locationSearch" placeholder="Enter a location or postcode">
            </div>
        </div>

        <Loader v-if="isLoading" />
        <div class="c-google-map" ref="googleMap" style=""></div>
        <Modal />
    </div>
</template>

<script>
    import GoogleMapsApiLoader from 'google-maps-api-loader'
    import axios from 'axios'
    import {EventBus} from '../event-bus'
    import {MapConfig} from "../map-config"
    import Modal from './Modal'
    import Loader from './Loader'

    export default {
        name: "MapApp",
        components: {
            Modal,
            Loader
        },
        props: {
            apiKey: {
                type: String,
                required: true
            }
        },
        data() {
            return {
                google: null,
                map: null,
                locations: [],
                markers: [],
                isLoading: true,
                searchResponse: ""
            }
        },
        async mounted() {
            const googleMapApi = await GoogleMapsApiLoader({
                apiKey: this.apiKey,
                libraries: ['places', 'geometry'],
            })
            this.google = googleMapApi
            this.initializeMap()
            this.fetchLocations()
            this.addAreaAltText()
            this.initSearchBox()
        },
        methods: {
            initializeMap() {
                const mapContainer = this.$refs.googleMap
                this.map = new this.google.maps.Map(
                    mapContainer, MapConfig
                )
            },
            fetchLocations() {
                axios.get(window.ajax.url, {
                    params: {
                        action: 'basethemeLocations'
                    }
                }).then(response => {
                    this.locations = response.data.locations
                    this.addMarkers()
                }).catch(error => {
                    EventBus.$emit('open-modal-with-error')
                })
            },
            openModal(location) {
                this.isLoading = true
                EventBus.$emit('open-modal', location)
            },
            addEventListenerToMarkers() {
                this.markers.forEach(marker => {
                    marker.googleMapMarker.addListener('click', () => {
                        this.map.setZoom(8);
                        this.map.setCenter(marker.googleMapMarker.getPosition());
                        this.openModal(marker.locationSlug)
                    })
                })
            },
            addMarkers() {
                if (this.locations.length > 0) {
                    this.locations.forEach(location => {
                        const position = new google.maps.LatLng(location.lat, location.lng);
                        this.markers.push({
                            googleMapMarker: new google.maps.Marker({
                                position: position,
                                map: this.map,
                                title: location.title,
                                animation: this.google.maps.Animation.DROP,
                            }),
                            ID: location.ID,
                            locationSlug: location.slug
                        })
                    })
                    this.addEventListenerToMarkers()

                    this.centerMap()
                }

                this.isLoading = false
            },
            centerMap() {
                let bounds = new google.maps.LatLngBounds();

                this.markers.forEach(marker => {
                    const latlng = new google.maps.LatLng(marker.googleMapMarker.position.lat(), marker.googleMapMarker.position.lng());
                    bounds.extend(latlng);
                })


                if (this.markers.length === 1) {
                    this.map.setCenter(bounds.getCenter());
                    map.setZoom(16);
                } else {
                    this.map.fitBounds(bounds);
                }
            },
            initSearchBox() {
                const searchField = this.$refs.locationSearch;
                const searchBox = new google.maps.places.Autocomplete(searchField, {
                    componentRestrictions: {country: 'gb'} // Restrict to just UK
                });
                searchBox.addListener("place_changed", () => {
                    this.searchResponse = '';
                    const place = searchBox.getPlace();

                    if (place.length == 0) return;

                    if (!place.geometry || !place.geometry.location) {
                        this.searchResponse = "Unable search with the location: '" + place.name + "'. Please try again or input a different location"
                        console.log("Returned place contains no geometry");
                        return;
                    }
                    
                    this.closestLocation({
                        name: place.formatted_address,
                        latLng: new google.maps.LatLng(place.geometry.location.lat(), place.geometry.location.lng())
                    });
                })
            },
            locateUser() {
                this.searchResponse = '';

                if (window.navigator) {
                    this.$refs.locationSearch = '';
                    navigator.geolocation.getCurrentPosition(position => {
                        this.closestLocation({
                            name: 'Your current location',
                            latLng: new google.maps.LatLng(position.coords.latitude, position.coords.longitude)
                        });
                    }, error => {
                        switch(error.code) {
                            case 1:
                                this.searchResponse = 'Location access denied. Please manually input a location';
                                break;
                           default:
                                this.searchResponse = 'Location currently unavailable. Please try again or manually input a location';
                                break;
                        }

                        console.warn(`ERROR(${error.code}): ${error.message}`);
                    });
                }
            },
            closestLocation(locationPoint) { 
                // Only locate closest if locations present
                if(this.locations.length) {
                    let distances = [];
                    let closest = -1;

                    // Finding the closest location
                    for(i = 0; i < this.locations.length; i++) {
                        let latlng = new google.maps.LatLng(this.locations[i].lat, this.locations[i].lng);
                        let dist = google.maps.geometry.spherical.computeDistanceBetween(latlng, locationPoint.latLng);

                        distances[i] = dist;
                        if (closest == -1 || dist < distances[closest] ) {
                            closest = i;
                        }
                    }

                    // Setting map bounds to show only closest
                    const bounds = new google.maps.LatLngBounds();
                    const closestLocation = this.locations[closest];
                    bounds.extend(closestLocation);
                    this.map.setCenter(bounds.getCenter());
                    this.map.fitBounds(bounds);
                    this.searchResponse = "Showing closest location(s) to '" + locationPoint.name + "'"
                } else {
                    this.map.setCenter(locationPoint.latLng)
                }
                
                this.map.setZoom(12)
            },
            addAreaAltText() {
                this.google.maps.event.addListener(this.map, 'tilesloaded', function(evt){
                    document.querySelectorAll('.c-google-map area').forEach(areaTag => {
                        if(!areaTag.alt || areaTag.alt ===""){
                            areaTag.alt = "Google Maps Area Marker - " + areaTag.title;
                        }
                    });
                });
            }
        }
    }
</script>
